import React, { useMemo } from "react";
import {
  ConfigProvider,
  Typography,
  Input,
  Button,
  Card,
  Table,
  Empty,
  Space,
  Tag,
  Spin,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { reviTheme } from "../../../../theme/reviTheme";
import { useFormatos } from "../../../../i18n/formatos";
import { useTherapistTerapiaController } from "./useTherapistTerapiaController";

import { ImpartirTerapiaWizardModal } from "../../../../components/Admin/TherapistCmp/TerapiaCmp";

import { confirmar } from "../../../../ui/confirmar";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { FilaTocable } from "../../../../components/revi/FilaTocable";
import { PaginacionCompacta } from "../../../../components/revi/PaginacionCompacta";
import { CardActionsMenu } from "../../../../components/Admin/uiComponents";
import "./Page_Therapist_Terapia.scss";

const { Title, Text } = Typography;

export function Page_Therapist_Terapia() {
  const navigate = useNavigate();
  // Dos namespaces: el de la terapia y el comun, que ya trae "Fecha", "Estado"
  // y "Cancelar".
  const { t } = useTranslation(["therapy", "common"]);
  const movil = useMovil();
  // La fecha de la tabla sigue al idioma de la interfaz, no a un "es-ES" fijo.
  const { fechaHora } = useFormatos();

  const {
    loading,
    sessions,
    pagination,
    search,
    setSearch,
    openWizard,
    setOpenWizard,
    onCreated,
    deletingId,
    onDeleteSession,
  } = useTherapistTerapiaController();

  const columns = useMemo(
    () => [
      {
        title: t("common:fields.date"),
        dataIndex: "createdAt",
        key: "createdAt",
        render: (_, s) => {
          const d = s?.createdAt || s?.startedAt;
          // La fecha sigue al idioma de la interfaz; antes llevaba "es-ES"
          // clavado y salia en espanol aunque el portal estuviera en frances.
          return fechaHora(d);
        },
      },
      {
        title: t("sesiones.columnas.terapia"),
        key: "therapy",
        render: (_, s) => s?.therapy?.title || "—",
      },
      {
        title: t("sesiones.columnas.usuarios"),
        key: "patients",
        // Contador de verdad: el "(s)" entre parentesis no existe en frances.
        render: (_, s) =>
          s?.patients?.length
            ? t("common:contadores.pacientes", { count: s.patients.length })
            : "—",
      },
      {
        title: t("common:fields.status"),
        dataIndex: "status",
        key: "status",
        render: (v) => {
          const val = String(v || "unknown").toLowerCase();

          const map = {
            active: { label: t("sesiones.estados.activa"), color: "green" },
            completed: {
              label: t("sesiones.estados.finalizada"),
              color: "default",
            },
            closed: {
              label: t("sesiones.estados.finalizada"),
              color: "default",
            },
            cancelled: {
              label: t("sesiones.estados.cancelada"),
              color: "red",
            },
          };

          const m = map[val] || { label: "—", color: "default" };
          return <Tag color={m.color}>{m.label}</Tag>;
        },
      },
      {
        title: "",
        key: "actions",
        align: "right",
        render: (_, s) => (
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(`/portal/therapist/terapia/sesion/${s._id}`)
              }
            >
              {t("sesiones.acciones.abrir")}
            </Button>

            {/*
              La confirmacion nombra la sesion que se va a borrar -terapia y
              fecha- en vez de decir solo "esta accion no se puede deshacer":
              con varias sesiones de la misma terapia en la lista, esa frase no
              distingue cual se esta borrando.
            */}
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === s._id}
              onClick={() =>
                confirmar({
                  titulo: t("sesiones.borrar.titulo"),
                  consecuencia: t("sesiones.borrar.descripcionObjeto", {
                    terapia: s?.therapy?.title || "—",
                    fecha: fechaHora(s?.createdAt || s?.startedAt),
                  }),
                  textoOk: t("sesiones.borrar.confirmar"),
                  onOk: () => onDeleteSession(s._id),
                })
              }
            >
              {t("sesiones.acciones.borrar")}
            </Button>
          </Space>
        ),
      },
    ],
    [navigate, deletingId, onDeleteSession, t, fechaHora],
  );

  const tablePagination = {
    current: pagination?.page ?? 1,
    pageSize: pagination?.limit ?? 10,
    total: pagination?.totalDocs ?? 0,
    onChange: pagination?.onChange,
    showSizeChanger: false,
  };

  return (
    <ConfigProvider theme={reviTheme}>
      <div className="revi-page-terapia">
        <div className="revi-page-terapia__header">
          <div>
            <Title level={2} style={{ marginBottom: 0 }}>
              {t("sesiones.titulo")}
            </Title>
            <Text type="secondary" className="revi-subtitulo-pagina">
              {t("sesiones.subtitulo")}
            </Text>
          </div>
        </div>

        <div className="revi-page-terapia__toolbar">
          <Input
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<SearchOutlined />}
            placeholder={t("sesiones.buscarPlaceholder")}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpenWizard(true)}
          >
            {t("sesiones.nueva")}
          </Button>
        </div>

        <Card className="revi-page-terapia__card" variant="outlined">
          {loading ? (
            <div className="revi-page-terapia__center">
              <Spin size="large" />
            </div>
          ) : sessions?.length ? (
            // Cinco columnas -fecha, terapia, usuarios, estado y acciones- no
            // caben en 375 px: la tabla se cortaba por la mitad y sus botones
            // quedaban fuera de la pantalla, sin forma de llegar a ellos.
            //
            // En movil cada sesion es una ficha, y la identidad es la TERAPIA,
            // que es lo que el terapeuta busca; la fecha y los pacientes bajan
            // a la linea de meta. Nunca se pintan las dos ramas a la vez.
            movil ? (
              <div className="revi-page-terapia__fichas">
                {sessions.map((s) => (
                  <SesionFicha
                    key={s._id}
                    sesion={s}
                    t={t}
                    fechaHora={fechaHora}
                    onAbrir={() =>
                      navigate(`/portal/therapist/terapia/sesion/${s._id}`)
                    }
                    onBorrar={() => onDeleteSession(s._id)}
                  />
                ))}

                {/* Mismo motivo que en el historial: las fichas no heredan la
                    paginacion de la tabla y las sesiones vienen de diez en
                    diez. */}
                <PaginacionCompacta
                  pagina={pagination?.page ?? 1}
                  total={pagination?.totalDocs ?? 0}
                  porPagina={pagination?.limit ?? 10}
                  onCambiar={pagination?.onChange}
                />
              </div>
            ) : (
              <Table
                rowKey="_id"
                columns={columns}
                dataSource={sessions}
                pagination={tablePagination}
              />
            )
          ) : (
            <div className="revi-page-terapia__center">
              <Empty description={t("sesiones.vacio")} />
            </div>
          )}
        </Card>

        <ImpartirTerapiaWizardModal
          open={openWizard}
          onCancel={() => setOpenWizard(false)}
          onCreated={onCreated}
        />
      </div>
    </ConfigProvider>
  );
}

/**
 * Una sesion, como ficha. Reutiliza las mismas piezas que las tarjetas del
 * portal para que una lista se parezca a otra.
 */
function SesionFicha({ sesion, t, fechaHora, onAbrir, onBorrar }) {
  const estados = {
    active: { label: t("sesiones.estados.activa"), color: "green" },
    completed: { label: t("sesiones.estados.finalizada"), color: "default" },
    closed: { label: t("sesiones.estados.finalizada"), color: "default" },
    cancelled: { label: t("sesiones.estados.cancelada"), color: "red" },
  };
  const estado = estados[String(sesion?.status || "").toLowerCase()];
  const titulo = sesion?.therapy?.title || "\u2014";
  const fecha = fechaHora(sesion?.createdAt || sesion?.startedAt);

  return (
    <Card className="revi-sesion-ficha">
      <FilaTocable
        alto={96}
        onTocar={onAbrir}
        aria-label={titulo}
        kebab={
          <CardActionsMenu
            ariaLabel={titulo}
            items={[
              {
                key: "abrir",
                icon: <EyeOutlined />,
                label: t("sesiones.acciones.abrir"),
                run: onAbrir,
              },
              {
                key: "del",
                icon: <DeleteOutlined />,
                label: t("sesiones.acciones.borrar"),
                peligroso: true,
                run: onBorrar,
                confirmacion: {
                  titulo: t("sesiones.borrar.titulo"),
                  consecuencia: t("sesiones.borrar.descripcionObjeto", {
                    terapia: titulo,
                    fecha,
                  }),
                  textoOk: t("sesiones.borrar.confirmar"),
                },
              },
            ]}
          />
        }
      >
        <div className="revi-sesion-ficha__texto">
          <span className="revi-sesion-ficha__titulo">{titulo}</span>

          <div className="revi-sesion-ficha__meta">
            {estado ? <Tag color={estado.color}>{estado.label}</Tag> : null}
            <span>{fecha}</span>
            {sesion?.patients?.length ? (
              <span>
                {"\u00b7 "}
                {t("common:contadores.pacientes", {
                  count: sesion.patients.length,
                })}
              </span>
            ) : null}
          </div>
        </div>
      </FilaTocable>
    </Card>
  );
}
